import React, { useState, useEffect, memo, useRef } from 'react';
import Modal from 'components/shared/Modal/Modal';
import ManageColumnsModalList from './ManageColumnsModalList';
import { useDispatch } from 'react-redux';
import ManageColumnsModalHeader from './ManageColumnsModalHeader';
import ManageColumnsModalFooter from './ManageColumnsModalFooter';
import { DEVICES_PAGE_TAB, LOADING_CIRCLE, USER_PREFERENCE_SETS } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';
import { patchUserPreference } from 'redux/UserPreferences';
import './ManageColumnsModal.css';
import ManageColumnsModalErrorBox from './ManageColumnsModalErrorBox';

const ManageColumnsModal = (props) => {
  const mountedRef = useRef(false);
  const { afterModalClose, animateModalClose, closeModal } = props;
  const dispatch = useDispatch();
  const [localCheckList, setLocalCheckList] = useState(null);
  const [updatingPreference, setUpdatingPreference] = useState(false);
  const [updatePreferenceError, setUpdatePreferenceError] = useState(null);
  let tabColumnsList = null;

  switch (props.activeTab) {
    case DEVICES_PAGE_TAB.pivot:
      tabColumnsList = props.columnsPreferences?.[0]?.state?.pivotTab;
      break;
    case DEVICES_PAGE_TAB.echo:
      tabColumnsList = props.columnsPreferences?.[0]?.state?.echoTab;
      break;
    case DEVICES_PAGE_TAB.all:
      tabColumnsList = props.columnsPreferences?.[0]?.state?.allDevicesTab;
      break;
    default:
      tabColumnsList = [];
      break;
  }

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (tabColumnsList) {
      let newLocalCheckList = {};
      tabColumnsList.forEach((column, idx) => {
        if (!column.disabled) newLocalCheckList[column.id] = column.selected;
      });
      setLocalCheckList(newLocalCheckList);
    }
  }, [props.activeTab, tabColumnsList]);

  const handleApplyButton = () => {
    const newColumnsPreferences = JSON.parse(JSON.stringify(props.columnsPreferences?.[0]));
    const preferenceId = newColumnsPreferences.preferenceId;
    switch (props.activeTab) {
      case DEVICES_PAGE_TAB.pivot:
        newColumnsPreferences?.state?.pivotTab &&
          newColumnsPreferences.state.pivotTab.forEach((column) => {
            if (column.id in localCheckList) {
              column.selected = localCheckList[column.id];
            }
          });
        break;
      case DEVICES_PAGE_TAB.echo:
        newColumnsPreferences?.state?.echoTab &&
          newColumnsPreferences.state.echoTab.forEach((column) => {
            if (localCheckList[column.id] !== null) {
              column.selected = localCheckList[column.id];
            }
          });
        break;
      case DEVICES_PAGE_TAB.all:
        newColumnsPreferences?.state?.allDevicesTab &&
          newColumnsPreferences.state.allDevicesTab.forEach((column) => {
            if (localCheckList[column.id] !== null) {
              column.selected = localCheckList[column.id];
            }
          });
        break;
      default:
        break;
    }

    setUpdatingPreference(true);

    dispatch(
      patchUserPreference({
        preferenceId,
        category: USER_PREFERENCE_SETS.COLUMNS_SET,
        name: newColumnsPreferences.name,
        default: newColumnsPreferences.default,
        state: newColumnsPreferences.state
      })
    )
      .then(() => {
        afterModalClose();
      })
      .catch((error) => {
        logger.error(error);
        if (mountedRef.current) {
          setUpdatePreferenceError(error);
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setUpdatingPreference(false);
        }
      });
  };

  let windowHeight = window.innerHeight;

  let modelWidth = 517;
  let modalHeight = 902;
  switch (props.activeTab) {
    case DEVICES_PAGE_TAB.pivot:
      modalHeight = windowHeight * 0.863;
      break;
    case DEVICES_PAGE_TAB.echo:
      modalHeight = windowHeight * 0.863;
      break;
    case DEVICES_PAGE_TAB.all:
      modalHeight = 420;
      break;
    default:
      return;
  }

  let overlay = null;

  if (updatePreferenceError) {
    overlay = (
      <ManageColumnsModalErrorBox
        data-tip={`Update Columns Preference Failed: ${updatePreferenceError}`}
        message={'Something went wrong. Please try again later.'}
        onClose={() => {
          setUpdatePreferenceError(null);
          afterModalClose();
        }}
      />
    );
  } else if (updatingPreference) {
    overlay = (
      <div id="Global_LoadingCircle_Overlay" className="Global_LoadingCircle_Overlay">
        {LOADING_CIRCLE}
      </div>
    );
  }

  return (
    <Modal
      global
      close={afterModalClose} //note that this only runs if you close the modal using the "closing" prop
      animate={animateModalClose}
      animateX={false}
      closing={closeModal}
      noPadding
      height={modalHeight}
      width={modelWidth}
    >
      {overlay}
      <ManageColumnsModalHeader />
      <ManageColumnsModalList
        activeTab={props.activeTab}
        tabColumnsList={tabColumnsList}
        localCheckList={localCheckList}
        setLocalCheckList={setLocalCheckList}
      />
      <ManageColumnsModalFooter
        handleApplyButton={handleApplyButton}
        afterModalClose={afterModalClose}
        updatingPreference={updatingPreference}
      />
    </Modal>
  );
};

export default memo(ManageColumnsModal);
